import { LegalPageShell, LegalSection } from '../components/LegalPageShell'
import { usePageMeta } from '../hooks/usePageMeta'

const UPDATED = '20 September 2026'

export function TermsPage() {
  usePageMeta(
    'Terms & Conditions | YellowDots',
    'Read the YellowDots Terms & Conditions governing use of our local learning discovery platform connecting learners with tutors, coaches and educators.',
  )
  return (
    <LegalPageShell title="Terms &amp; Conditions" updated={UPDATED}>
      <p>
        These Terms &amp; Conditions ("Terms") govern your access to and use of YellowDots AI, a
        local learning discovery network operated by mySATHI Foundation ("YellowDots", "we",
        "us"), available at yellowdots.ai and through our mobile and web applications
        (collectively, the "Platform"). By creating an account or otherwise using the Platform, you
        agree to be bound by these Terms and by our <a href="/privacy" className="font-semibold text-dot-700 underline underline-offset-2">Privacy Policy</a>, which is incorporated
        here by reference. The Platform facilitates discovery and connection only - it does not
        facilitate financial transactions and is not a marketplace.
      </p>

      <LegalSection title="1. What YellowDots is">
        <p>
          YellowDots helps people looking to learn ("learners") discover nearby tutors, coaches,
          trainers, schools, colleges and companies ("providers"), and helps providers be found by
          learners in their area. We provide the discovery and connection layer - a map of
          participants, matching by category and location, and a way to request and accept
          connections. We are not a party to any tuition, coaching, admission, employment or other
          arrangement that learners and providers agree to after connecting, and we do not
          guarantee the outcome, quality, safety or legality of any such arrangement.
        </p>
      </LegalSection>

      <LegalSection title="2. Definitions">
        <ul className="list-disc space-y-1.5 pl-5">
          <li><span className="font-semibold text-deep-900">"Account"</span> - the relationship between you and the Platform, created on registration, that enables login, profile creation and use of Platform features.</li>
          <li><span className="font-semibold text-deep-900">"Connection"</span> - a request from one user to another - a learner to a provider, or a provider to another provider - accepted by the other party, following which personal data is exchanged with both parties' consent.</li>
          <li><span className="font-semibold text-deep-900">"Personal Data"</span> - any data about an individual who is identifiable by or in relation to such data, as defined under the Digital Personal Data Protection Act, 2023 ("DPDP Act").</li>
          <li><span className="font-semibold text-deep-900">"Profile"</span> - the information you create and maintain on the Platform, including personal data and how you describe your needs or offerings.</li>
          <li><span className="font-semibold text-deep-900">"Provider"</span> - a user who lists and offers tuition, coaching, training or related services. A provider may be an individual (a tutor) or an organisation (a centre, school, college or company).</li>
          <li><span className="font-semibold text-deep-900">"Learner"</span> - a user who discovers and connects with providers, or the parent or guardian managing such an account on a minor's behalf.</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Eligibility and accounts">
        <p>
          You must provide accurate registration details and keep your password confidential. You
          are responsible for all activity under your account. One account may hold at most two
          linked profiles - a learner side and a provider side - and you must not create accounts
          for anyone else without their knowledge, except as a parent or guardian creating or
          linking an account for your own child as described below, or where an administrator
          creates provider accounts in bulk on behalf of an organisation that has separately
          arranged for this with us.
        </p>
        <p>
          A learner under 18 years of age may register, but a parent or guardian must be linked to
          the account, and every connection request that learner sends or receives requires that
          guardian's approval before it takes effect. A guardian who links to a minor's account
          confirms they have the authority to consent on that child's behalf. Where a person with
          disability has a lawful guardian under the Rights of Persons with Disabilities Act 2016 or
          any other law, that guardian may register and manage an account on their behalf.
        </p>
        <p>
          Before your account is activated, you can review and correct the information in your
          profile, and you can update it at any time afterwards from your account settings. By
          confirming your profile, you represent that the information you have provided is true and
          correct to the best of your knowledge.
        </p>
      </LegalSection>

      <LegalSection title="4. Connections and visibility">
        <p>
          A connection is initiated when one party sends a request to another. Before sending a
          request, you will be shown what personal data will be shared if it is accepted. When you
          receive a request, accepting it confirms that your own personal data will be shared with
          the requesting party - a connection is never completed without active confirmation from
          both sides, and this exchange cannot be reversed once made. You may cancel a request at
          any time before it is accepted. See our{' '}
          <a href="/privacy" className="font-semibold text-dot-700 underline underline-offset-2">Privacy Policy</a>{' '}
          for what is visible before and after a connection.
        </p>
      </LegalSection>

      <LegalSection title="5. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Impersonate another person or organisation, or misrepresent your qualifications, affiliation or identity;</li>
          <li>Use the Platform to harass, threaten, defraud or discriminate against another user based on caste, religion, race, gender, sexual orientation, disability, or any other status protected under applicable law;</li>
          <li>Post content that is defamatory, unlawful, obscene, hateful, or that promotes or glorifies self-harm, suicide or violence;</li>
          <li>Submit synthetically generated content - including deepfakes, AI-generated or AI-altered images, or cloned audio - that misrepresents a real person or could mislead another user about your identity or qualifications;</li>
          <li>Scrape, copy or redistribute other users' contact details or listings outside the Platform, or deploy bots or automated tools to extract or manipulate content;</li>
          <li>Solicit another user's login credentials, or attempt to bypass the connection-approval flow, including the guardian-approval gate for a minor's account;</li>
          <li>Send unsolicited advertisements, spam, or junk communications to other users;</li>
          <li>Remove, circumvent or interfere with any security feature of the Platform, or upload viruses or other malicious code;</li>
          <li>Upload content that is unlawful, infringing, or that you do not have the right to share; or</li>
          <li>Use the Platform for any purpose other than genuine learning discovery and connection.</li>
        </ul>
        <p>
          When you initiate or accept a connection, you consent to your name, phone number and
          email address being shared with the other party. Once shared, that party handles your
          personal data under their own practices - we are not responsible for how they use or
          safeguard it afterwards. Use contact details received through a connection only for the
          purpose the connection was made for, and keep them confidential.
        </p>
        <p>
          We may suspend or terminate accounts that violate these Terms, misrepresent verification
          details, or put other users' safety at risk. We will make reasonable efforts to notify
          you before doing so, except where we are required to act immediately by law or to prevent
          harm.
        </p>
      </LegalSection>

      <LegalSection title="6. Verification and trust">
        <p>
          Providers may submit documents or details for verification, and the Platform may display
          a verification tier alongside a provider's listing. A verification tier reflects the
          checks we were able to perform at the time it was granted; it is not a guarantee of a
          provider's ongoing conduct, and learners and guardians should exercise their own judgment
          before proceeding with any arrangement.
        </p>
      </LegalSection>

      <LegalSection title="7. Content and intellectual property">
        <p>
          You retain ownership of the profile details, descriptions and other content you submit.
          By posting it, you grant YellowDots a non-exclusive, royalty-free licence to host,
          store, reproduce and display it to other users as part of the Platform's ordinary
          operation - for example, showing a provider's public profile to a learner who searches
          nearby. You are responsible for the accuracy of what you post.
        </p>
        <p>
          We may remove any content or suspend any account at our discretion, particularly where
          content is unlawful, misleading, discriminatory or otherwise violates these Terms.
          Content that sexually exploits minors will be removed within 24 hours of coming to our
          knowledge and reported to the relevant authorities. If you believe content on the
          Platform infringes your rights or violates applicable law, raise it through Section 10.
        </p>
        <p>
          All rights in the Platform itself - its software, design, features and branding - are
          owned by or licensed to YellowDots. You may not copy, reproduce or use our branding or
          materials without our prior written consent.
        </p>
      </LegalSection>

      <LegalSection title="8. Disclaimers, liability and indemnity">
        <p>
          The Platform is provided "as is". We do not vet every interaction between users, and we
          are not responsible for the conduct of any learner or provider, on or off the Platform,
          or for any arrangement they enter into. To the maximum extent permitted by law, Yellow
          Dots and mySATHI Foundation are not liable for any indirect, incidental or consequential
          loss arising from your use of the Platform, or for any disruption caused by circumstances
          beyond our reasonable control.
        </p>
        <p>
          You agree to indemnify and hold harmless YellowDots, mySATHI Foundation, and their
          officers, employees and agents from any claim, liability or expense arising from your
          breach of these Terms or your use of the Platform in a manner that causes harm to any
          person or violates applicable law.
        </p>
      </LegalSection>

      <LegalSection title="9. Managing your account">
        <p>
          You can update most of your profile at any time from your account settings. To request
          deletion of your account, write to{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>{' '}
          from your registered email address. Personal data you have already shared with another
          user through an accepted connection remains with that party and is not affected by your
          account's deletion. See our{' '}
          <a href="/privacy" className="font-semibold text-dot-700 underline underline-offset-2">Privacy Policy</a>{' '}
          for how long we keep your data after deletion.
        </p>
      </LegalSection>

      <LegalSection title="10. Grievances">
        <p>
          If you have a concern about the Platform, another user's conduct, or any other matter
          related to these Terms, contact us at{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>.
          We will endeavour to acknowledge your complaint within 24 hours and resolve it within 15
          days, in accordance with the Information Technology (Intermediary Guidelines and Digital
          Media Ethics Code) Rules 2021. If your concern relates to your personal data, see the
          grievance process in our{' '}
          <a href="/privacy" className="font-semibold text-dot-700 underline underline-offset-2">Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be reflected by
          updating the date at the top of this page, and continued use of the Platform after a
          change takes effect constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection title="12. Governing law">
        <p>
          These Terms are governed by the laws of India, and any dispute arising from them is
          subject to the exclusive jurisdiction of the courts at Delhi, India. These Terms
          constitute a valid electronic record and electronic contract under the Information
          Technology Act 2000 and the rules made thereunder.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact">
        <p>
          Questions about these Terms can be sent to{' '}
          <a href="mailto:support@yellowdots.ai" className="font-semibold text-dot-700 underline underline-offset-2">support@yellowdots.ai</a>.
        </p>
      </LegalSection>
    </LegalPageShell>
  )
}
